import matter from 'gray-matter';
import { marked } from 'marked';
import type { Entry, EntryFrontmatter } from '@/types/radar';

/**
 * Parses markdown files with YAML frontmatter to create radar entries
 */
export class MarkdownParser {
  /**
   * Parse a single markdown file content
   */
  parseEntry(content: string, filePath: string): Entry {
    const { data, content: markdownContent } = matter(content);
    const frontmatter = data as EntryFrontmatter;

    // Validate required fields
    if (!frontmatter.title || !frontmatter.ring || !frontmatter.quadrant) {
      throw new Error(
        `Invalid markdown file ${filePath}: missing required frontmatter (title, ring, quadrant)`
      );
    }

    // Generate ID from file path
    const id = this.generateId(filePath);

    // Parse markdown to HTML
    const description = marked.parse(markdownContent.trim()) as string;

    return {
      id,
      title: frontmatter.title,
      quadrant: frontmatter.quadrant,
      ring: frontmatter.ring,
      description,
      tags: frontmatter.tags || [],
      url: frontmatter.url,
      featured: frontmatter.featured || false,
      filePath,
      lastModified: new Date(),
    };
  }

  /**
   * Parse multiple markdown files
   */
  async parseEntries(files: Array<{ path: string; content: string }>): Promise<Entry[]> {
    const entries: Entry[] = [];

    for (const file of files) {
      try {
        const entry = this.parseEntry(file.content, file.path);
        entries.push(entry);
      } catch (error) {
        console.error(`Error parsing ${file.path}:`, error);
        // Continue parsing other files
      }
    }

    return entries;
  }

  /**
   * Generate unique ID from file path
   * Example: radar/2024-01/typescript.md -> typescript
   * Example: radar/kubernetes.md -> kubernetes
   */
  private generateId(filePath: string): string {
    const fileName = filePath.split('/').pop() || filePath;
    return fileName.replace(/\.md$/, '').toLowerCase();
  }

  /**
   * Validate frontmatter structure
   */
  validateFrontmatter(frontmatter: unknown): frontmatter is EntryFrontmatter {
    if (typeof frontmatter !== 'object' || frontmatter === null) {
      return false;
    }

    const fm = frontmatter as Record<string, unknown>;

    if (typeof fm.title !== 'string') return false;
    if (!['adopt', 'trial', 'assess', 'hold'].includes(fm.ring as string)) return false;
    if (
      !['languages-frameworks', 'tools', 'platforms', 'techniques'].includes(
        fm.quadrant as string
      )
    ) {
      return false;
    }

    return true;
  }
}
